function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function isPackageExpired(pkg = {}) {
  if (!pkg) return true;
  if (String(pkg.status || "").toLowerCase() === "expired") return true;
  const ms = toMillis(pkg.expiresAt);
  return !!ms && ms < Date.now();
}

export function normalizeActivePackages(userDoc = {}) {
  const rawPackages = Array.isArray(userDoc?.activeStorePackages)
    ? userDoc.activeStorePackages
    : [];

  const fallback =
    rawPackages.length === 0 && userDoc?.storePackage?.packageId
      ? [userDoc.storePackage]
      : [];

  return [...rawPackages, ...fallback]
    .filter((pkg) => pkg?.packageId)
    .map((pkg) => ({
      ...pkg,
      expired: isPackageExpired(pkg),
    }))
    .filter((pkg, index, arr) => {
      const firstIndex = arr.findIndex((entry) => {
        const sameId = entry.packageId === pkg.packageId;
        const sameExpiry = toMillis(entry.expiresAt) === toMillis(pkg.expiresAt);
        const sameSource = String(entry.source || "") === String(pkg.source || "");
        return sameId && sameExpiry && sameSource;
      });
      return firstIndex === index;
    });
}

export function summarizeStorePackages(userDoc = {}) {
  const allPackages = normalizeActivePackages(userDoc);
  const activePackages = allPackages.filter((pkg) => !pkg.expired);
  const sourcePackages = activePackages.length > 0 ? activePackages : allPackages;

  const totals = sourcePackages.reduce(
    (acc, pkg) => {
      acc.courseLimit += Number(pkg.courseLimit || 0);
      acc.productLimit += Number(pkg.productLimit || 0);
      acc.serviceLimit += Number(pkg.serviceLimit || 0);
      acc.totalPackages += 1;

      const expiry = toMillis(pkg.expiresAt);
      if (expiry > acc.latestExpiryMs) {
        acc.latestExpiryMs = expiry;
        acc.expiresAt = pkg.expiresAt || null;
      }
      return acc;
    },
    {
      courseLimit: 0,
      productLimit: 0,
      serviceLimit: 0,
      totalPackages: 0,
      latestExpiryMs: 0,
      expiresAt: null,
    },
  );

  const latestPackage =
    sourcePackages
      .slice()
      .sort((a, b) => toMillis(b.expiresAt) - toMillis(a.expiresAt))[0] || null;

  return {
    allPackages,
    activePackages,
    latestPackage,
    hasActivePackage: activePackages.length > 0,
    summary: {
      packageId: latestPackage?.packageId || userDoc?.storePackage?.packageId || "",
      name:
        activePackages.length > 1
          ? `${activePackages.length} active packages`
          : latestPackage?.name || userDoc?.storePackage?.name || "",
      courseLimit: totals.courseLimit,
      productLimit: totals.productLimit,
      serviceLimit: totals.serviceLimit,
      expiresAt: totals.expiresAt,
      totalPackages: totals.totalPackages,
      status:
        activePackages.length > 0 ? "active" : allPackages.length > 0 ? "expired" : "inactive",
    },
  };
}

export function buildPackageEntry(pkg = {}, overrides = {}) {
  return {
    packageId: pkg.id || pkg.packageId || "",
    name: pkg.name || "",
    courseLimit: Number(pkg.courseLimit || 0),
    productLimit: Number(pkg.productLimit || 0),
    serviceLimit: Number(pkg.serviceLimit || 0),
    price: Number(pkg.price || 0),
    currency: pkg.currency || "LKR",
    durationDays: Number(pkg.durationDays || 30),
    approvedAt: overrides.approvedAt || new Date().toISOString(),
    expiresAt: overrides.expiresAt || null,
    source: overrides.source || pkg.source || "manual",
    status: "active",
  };
}
